import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  icon?: React.ReactNode;
  label: string;
  allLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (value: string[]) => void;
  triggerClassName?: string;
  hideLabel?: boolean;
}

export function MultiSelectFilter({
  icon,
  label,
  allLabel,
  searchPlaceholder,
  emptyText,
  options,
  selected,
  onChange,
  triggerClassName,
  hideLabel = false,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  const selectedLabels = options.filter((option) => selected.includes(option.value));
  const valueLabel =
    selectedLabels.length === 0
      ? allLabel
      : selectedLabels.length === 1
        ? (selectedLabels[0]?.label ?? allLabel)
        : `${selectedLabels.length} selected`;

  const toggleOption = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={hideLabel ? `${label}: ${valueLabel}` : undefined}
          title={hideLabel ? label : undefined}
          className={`h-10 min-w-[180px] justify-between gap-2 bg-card px-3 font-normal ${triggerClassName ?? ''}`}
        >
          <span className="flex min-w-0 items-center gap-2">
            {icon}
            {hideLabel ? null : (
              <span className="shrink-0 text-xs font-semibold uppercase text-text-subtle">
                {label}:
              </span>
            )}
            <span className="truncate text-sm text-text-main">{valueLabel}</span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-text-subtle" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 overflow-hidden p-0" align="start" sideOffset={4}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => toggleOption(option.value)}
                >
                  <Checkbox
                    checked={selected.includes(option.value)}
                    className="pointer-events-none"
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 ? (
          <div className="flex justify-end border-t border-border-subtle p-2">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
